/**
 *  AreaChart script file
 **/
import * as d3 from 'd3'

export default {
  name: 'AreaChart',
  mixins: [],
  components: {},
  props: {
    margin: {
      type: Object,
      default: () => {
        return {
          top: 0, right: 0, bottom: 0, left: 0
        }
      },
      required: false
    },
    chartData: {
      type: Array,
      required: true
    },
    type: {
      type: String,
      default: 'line',
      validator (value) {
        const positions = [
          'line',
          'area'
        ]
        return positions.indexOf(value) !== -1
      },
      required: false
    },
    color: {
      type: Array,
      required: true
    },
    lineType: {
      type: String,
      default: 'solid',
      validator (value) {
        const positions = [
          'dashed',
          'solid'
        ]
        return positions.indexOf(value) !== -1
      },
      required: false
    },
    // TODO: for this project
    kpi: {
      type: String
    },
    ctrBench: {
      type: Number,
      required: false
    }
  },
  watch: {
    margin () {
      this.clearChart()
    },
    chartData () {
      this.clearChart()
    },
    type () {
      this.clearChart()
    },
    color () {
      this.clearChart()
    },
    lineType () {
      this.clearChart()
    },
    kpi () {
      this.clearChart()
    },
    ctrBench () {
      this.clearChart()
    }
  },
  data () {
    return {
      container: null,
      svg: null,
      date: null,
      width: null,
      height: null,
      xScale: null,
      yScale: null,
      tooltipDate: null,
      crosshair: null
    }
  },
  mounted () {
    this.createChart()

    window.addEventListener('resize', this.clearChart, false)
  },
  computed: {},
  methods: {
    createChart () {
      if (!this.$refs.container) {
        return
      }
      this.container = this.$refs.container.getBoundingClientRect()
      this.width = this.container.width - this.margin.left - this.margin.right
      this.height = this.container.height - this.margin.top - this.margin.bottom - 2

      this.svg = d3.select(this.$refs.svg)
        .attr('width', `${this.container.width}px`)
        .attr('height', `${this.container.height}px`)

      const tooltip = d3.select(this.$refs.tooltip)

      this.crosshair = this.svg.append('line')
        .attr('class', 'crosshair')
        .attr('stroke-width', 'rgba(26, 30, 41, 0.4)')
        .attr('stroke', 'rgba(26, 30, 41, 0.4)')
        .style('opacity', 0)

      const g = this.svg
        .append('g')
        .attr('transform', `translate(${this.margin.left}, ${this.margin.top + 1})`)

      this.xScale = d3.scaleLinear().rangeRound([0, this.width])
      this.yScale = d3.scaleLinear().rangeRound([this.height, 0])
      const colorScale = d3.scaleOrdinal().range(this.color)
      const line = d3.line().x(d => this.xScale(d.date)).y(d => this.yScale(d.value)).curve(d3.curveMonotoneX)
      const area = d3.area().x(d => this.xScale(d.date)).y1(d => this.yScale(d.value)).y0(this.yScale(0)).curve(d3.curveMonotoneX)
      const parseDate = d3.timeFormat('%d/%m/%Y')

      this.chartData.forEach((el, index) => {
        const dat = el.values
        const max = d3.max(dat, d => d.value)
        this.xScale.domain(d3.extent(dat, d => d.date))
        this.yScale.domain([0, max !== 0 ? max : 100])

        if (this.type === 'area') {
          g.append('path')
            .datum(dat)
            .attr('class', 'area' + this.chartData[index].name)
            .attr('fill', d => colorScale(index))
            .attr('opacity', 0.12)
            .attr('d', area)
        }

        g.append('path')
          .datum(dat)
          .attr('line', 'line' + this.chartData[index].name)
          .attr('class', 'line ' + this.lineType)
          .attr('fill', 'none')
          .attr('stroke', d => colorScale(index))
          .attr('stroke-linejoin', 'round')
          .attr('stroke-linecap', 'round')
          .attr('stroke-width', 1)
          .attr('d', line)
      })

      if (this.ctrBench) {
        g.append('line')
          .attr('stroke', 'var(--colorFeedError)')
          .attr('x1', 0)
          .attr('y1', this.yScale(this.ctrBench))
          .attr('x2', this.width)
          .attr('y2', this.yScale(this.ctrBench))
      }

      const rect = g.append('rect')
        .attr('width', `${this.container.width}px`)
        .attr('height', `${this.container.height}px`)
        .attr('opacity', 0)

      rect.on('mousemove', () => {
        d3.event.preventDefault()

        if (this.chartData[0].values.length === 0) {
          return
        }

        const mouse = d3.mouse(d3.event.target)
        const date = this.xScale.invert(mouse[0])
        const bisectDate = d3.bisector(d => d.date).left
        const i = bisectDate(this.chartData[0].values, date)
        const d0 = (this.chartData[0].values[i - 1] || this.chartData[0].values[0]).date
        const d1 = this.chartData[0].values[i].date
        const d = date - d0 > d1 - date ? d1 : d0

        if (d3.event.pageX - this.container.left > this.container.width * 0.5) {
          tooltip.style('transform', 'translate3d(-100%, 0, 0)')
        } else {
          tooltip.style('transform', 'translate3d(0, 0, 0)')
        }

        this.tooltipDate = parseDate(d)
        tooltip.style('opacity', 1)
        tooltip.style('left', this.xScale(date) + 'px')
          .style('top', (d3.event.offsetY - 32) + 'px')

        this.drawCrosshair(d)
        this.$emit('onMouseMove', { kpi: this.kpi, date: d })
      })

      rect.on('mouseout', () => {
        tooltip.style('opacity', 0)

        this.hideCrosshair()
        this.$emit('onMouseOut', this.kpi)
      })
    },
    drawCrosshair (date) {
      this.crosshair.style('opacity', 1)
        .attr('x1', this.xScale(date))
        .attr('y1', -32) // NOTE: only for this project
        // .attr('y1', this.margin.top)
        .attr('x2', this.xScale(date))
        .attr('y2', 64) // NOTE: only for this project
      // .attr('y2', this.height - this.margin.bottom)
    },
    hideCrosshair () {
      this.crosshair.style('opacity', 0)
    },
    clearChart () {
      while (this.$refs.svg && this.$refs.svg.lastChild) {
        this.$refs.svg.removeChild(this.$refs.svg.lastChild)
      }

      this.createChart()
    }
  }
}
